import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { ImagePickerAsset } from 'expo-image-picker';
import type { TFunction } from 'i18next';
import type { z } from 'zod';
import { array, custom, email, object, string } from 'zod';

import { MAX_SIZE } from '@/features/media/constants/image';
import type { AvatarPickerResult } from '@/features/media/types/picker';
import type { FactEntry } from '@/features/profile-setting/components/organization-edit/FactList';
import { DESCRIPTION_MAX_LENGTH } from '@/features/profile-setting/components/organization-edit/MultilineEditorModal';
import type {
  OrganizationEditFormValues,
  TerritorialAuthorityEditFormValues
} from '@/features/profile-setting/components/organization-edit/types';
import type { ActiveExtensionData } from '@/features/profile-setting/components/organization-extensions/fragmentTypes';
import {
  getAllExtensions,
  validateExtensionData
} from '@/features/profile-setting/components/organization-extensions/registry';
import {
  isValidAreaSquareKm,
  isValidOrganizationPhone,
  isValidOrganizationUrl,
  isValidPopulation
} from '@/features/profile-setting/utils/organizationFormValidation';
import { isEmptyOrNull } from '@/shared/utils/string';
import { maxFileSizeValidator } from '@/shared/validation/inputValidation';

type EditTFunction = TFunction<'profile-setting', 'screens.organization-edit'>;

/**
 * Per-item image validation. Issues carry their item index in `path` so
 * errors land at `errors.detailImages[i]` in react-hook-form. Validated on
 * submit; after array mutations, DetailImageList re-triggers validation for
 * `detailImages` (the documented pattern for dependent validation).
 *
 * An entry is valid iff it resolved to a media item id (existing backend
 * image or successfully uploaded temporary item) and the originally picked
 * file is within the size limit. The original `asset.fileSize` is checked
 * (not the compressed upload size) so an oversized pick is flagged even when
 * client-side compression would have made the upload succeed.
 *
 * `mediaItem`/`asset` are opaque objects — `custom` passes them through
 * without structural checks.
 */
const detailImagesSchema = (t: EditTFunction) =>
  array(
    object({
      key: string(),
      mediaItem: custom<ClientMediaItem>().optional(),
      temporaryMediaItemId: string().optional(),
      asset: custom<ImagePickerAsset>().optional()
    })
  ).superRefine((entries, ctx) => {
    for (const [index, entry] of entries.entries()) {
      if (!entry.mediaItem?.id && !entry.temporaryMediaItemId) {
        ctx.addIssue({
          code: 'custom',
          path: [index],
          message: t('errors.imageUploadFailed')
        });
      } else if (
        maxFileSizeValidator(entry.asset?.fileSize, {
          maxInBytes: MAX_SIZE
        }) !== true
      ) {
        ctx.addIssue({
          code: 'custom',
          path: [index],
          message: t('errors.imageTooLarge')
        });
      }
    }
  });

const optionalUrl = (t: EditTFunction) =>
  string()
    .optional()
    .refine(
      (v) => isEmptyOrNull((v ?? '').trim()) || isValidOrganizationUrl(v ?? ''),
      t('errors.urlInvalid')
    );

const baseShape = (t: EditTFunction) => ({
  name: string().trim().min(1, t('errors.nameRequired')),
  descriptionText: string().max(
    DESCRIPTION_MAX_LENGTH,
    t('errors.descriptionTooLong', { max: DESCRIPTION_MAX_LENGTH })
  ),
  detailImages: detailImagesSchema(t),
  // Validated by the form's own UI state (hasIncompleteFact); pass through.
  facts: array(custom<FactEntry>()),
  logoResult: custom<AvatarPickerResult>()
});

export const organizationEditSchema = (t: EditTFunction) =>
  object({
    ...baseShape(t),
    address: string().optional(),
    emailAddress: string()
      .optional()
      .refine(
        (v) =>
          isEmptyOrNull((v ?? '').trim()) ||
          email().safeParse((v ?? '').trim()).success,
        t('errors.emailAddressInvalid')
      ),
    phone: string()
      .optional()
      .refine(
        (v) =>
          isEmptyOrNull((v ?? '').trim()) || isValidOrganizationPhone(v ?? ''),
        t('errors.phoneInvalid')
      ),
    url: optionalUrl(t)
  }) satisfies z.ZodType<OrganizationEditFormValues>;

export const territorialAuthorityEditSchema = (t: EditTFunction) =>
  object({
    ...baseShape(t),
    url: optionalUrl(t),
    data: custom<ActiveExtensionData>(),
    population: string().refine(
      isValidPopulation,
      t('errors.populationInvalid')
    ),
    areaSquareKm: string().refine(
      isValidAreaSquareKm,
      t('errors.areaSquareKmInvalid')
    )
  }).superRefine((data, ctx) => {
    validateExtensionData(data.data, getAllExtensions(), t, ctx, ['data']);
  }) satisfies z.ZodType<TerritorialAuthorityEditFormValues>;
